import React, { useState, useEffect, RefObject } from 'react';
import styled from 'styled-components';
import { gridConfig } from '@tvg/fuse-design';
import AlertsComponent from 'components/Alert/AlertsComponent';
import Button from 'components/Button/Button';
import debounce from '@sd-utility/debounce';
import { useExperimentsContext } from 'context/ExperimentsContext';
import StarRating from 'components/RatingsAndReviews/StarRating';
import Price from 'components/Price';
import { ProductPriceAmount } from 'types/Product';
import DynamicPrice from 'components/DynamicPrice';
import StockMessageDisplay from 'components/StockMessage/StockMessageDisplay';
import { useSelectedSkuContext } from 'context/SelectedSku/SelectedSkuContext';

const Container = styled.div<{ containerIsVisible: boolean }>`
  visibility: hidden;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: var(--spacing-space16);
  background-color: white;
  transition: all 300ms ease-out;
  transform: translateY(100%);
  box-shadow:
    0px -3px 6px rgba(0, 0, 0, 0.15),
    0px -2px 4px rgba(0, 0, 0, 0.12);
  z-index: 10;

  ${({ containerIsVisible }) =>
    containerIsVisible &&
    `
    visibility: visible;
    transform: translateY(0%);
  `}

  @media (min-width: ${gridConfig.l.minWidth}) {
    display: none;
  }
`;

const ProductInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const PriceContainer = styled.div`
  margin-right: var(--spacing-space16);

  h2,
  h2 + p,
  p:not(:last-child) {
    font-size: var(--font-size-2);
    line-height: var(--font-line-height-2);
  }

  p {
    margin: 0;
  }

  .fuse-price__prev-price {
    display: none;
  }

  > *,
  .fuse-price__sale-price-wrapper {
    margin-bottom: 0 !important;
  }
`;

const StarRatingContainer = styled.div`
  flex-shrink: 0;
  white-space: nowrap;

  > * {
    margin-bottom: 0;
  }
`;

const StockMessageContainer = styled.div`
  p:last-child {
    font-size: var(--font-size-m2);
    line-height: var(--font-line-height-m2);
  }
`;

const ErrorContainer = styled.div`
  margin-top: var(--spacing-space8);
  margin-bottom: var(--spacing-space12);
`;

const ButtonContainer = styled.div`
  margin-top: var(--spacing-space8);

  p:last-child {
    font-size: var(--font-size-m2);
    line-height: var(--font-line-height-m2);
  }
`;

const lessThan100PxFromBottomOfPage = () => window.innerHeight + window.pageYOffset >= document.body.offsetHeight - 100;

interface Props {
  primaryAddToBasketButtonRef: RefObject<HTMLDivElement>;
  handleAddToBasket: (id: string, label: string) => void;
  disabled: boolean;
  updateBasket?: boolean;
  errorMessage?: string;
  productData: {
    price: ProductPriceAmount;
    reviewTotal?: number;
    reviewStars?: number;
  };
}

function StickyAddToBasketButton({
  primaryAddToBasketButtonRef,
  handleAddToBasket,
  disabled,
  updateBasket = false,
  errorMessage,
  productData,
}: Props) {
  const [containerIsVisible, setContainerIsVisible] = useState(false);
  const experiments = useExperimentsContext();
  const challengerEnabled = experiments['se-8'];
  const { selectedSku, selectedInsurance, isAddOnSelected } = useSelectedSkuContext();

  const renderPrice = () => {
    const shouldShowDynamicPrice =
      (selectedSku && productData.price?.currentFrom) ||
      (selectedSku && ((selectedInsurance && typeof selectedInsurance !== 'string') || isAddOnSelected));

    return (
      <PriceContainer data-testid="sticky_price_container">
        {shouldShowDynamicPrice ? (
          <DynamicPrice price={productData.price} force />
        ) : (
          <Price price={productData.price} />
        )}
      </PriceContainer>
    );
  };

  const renderStarRating = () => {
    if (!productData?.reviewTotal || productData?.reviewTotal <= 0 || !productData?.reviewStars) {
      return null;
    }

    return (
      <StarRatingContainer data-testid="sticky_star_rating_container">
        <StarRating reviewTotal={productData.reviewTotal} reviewStars={productData.reviewStars} size="medium" />
      </StarRatingContainer>
    );
  };

  const renderStockMessage = () => (
    <StockMessageContainer data-testid="sticky_stock_message_container">
      <StockMessageDisplay />
    </StockMessageContainer>
  );

  const renderProductInfo = () => {
    if (!challengerEnabled) return null;

    return (
      <>
        <ProductInfoRow>
          {renderPrice()}
          {renderStarRating()}
        </ProductInfoRow>

        {renderStockMessage()}
      </>
    );
  };

  const renderErrorMessage = () => {
    if (!errorMessage) return null;

    const errorComponent = <AlertsComponent errorMessage={errorMessage} />;

    if (challengerEnabled) {
      return <ErrorContainer>{errorComponent}</ErrorContainer>;
    }

    return errorComponent;
  };

  const renderBasketButton = () => {
    const button = (
      <Button
        id="stickyAddToBasketButton"
        onClick={() => handleAddToBasket('stickyAddToBasketButton', 'Sticky add to basket')}
        data-testid="sticky_add_to_basket_button"
        disabled={disabled}
        size={{
          xxs: 'medium',
          m: 'medium',
          xl: 'large',
        }}
        data-transaction-name={challengerEnabled ? 'PersistentATB' : 'NonPersistentATB'}
      >
        {updateBasket ? 'Update basket' : 'Add to basket'}
      </Button>
    );

    if (challengerEnabled) {
      return <ButtonContainer>{button}</ButtonContainer>;
    }

    return button;
  };

  const toggleStickyAddToBasketVisibility = debounce(() => {
    if (!primaryAddToBasketButtonRef.current) return;

    const { height, y } = primaryAddToBasketButtonRef.current.getBoundingClientRect();
    const scrolledAbovePrimaryAddToBasketButton = height > -y;

    if (scrolledAbovePrimaryAddToBasketButton || lessThan100PxFromBottomOfPage()) {
      setContainerIsVisible(false);
    } else {
      setContainerIsVisible(true);
    }
  }, 50);

  useEffect(() => {
    if (challengerEnabled) {
      setContainerIsVisible(true);
    } else {
      toggleStickyAddToBasketVisibility();
      window.addEventListener('scroll', toggleStickyAddToBasketVisibility);
    }

    return () => {
      window.removeEventListener('scroll', toggleStickyAddToBasketVisibility);
    };
  }, [challengerEnabled, toggleStickyAddToBasketVisibility]);

  return (
    <Container containerIsVisible={containerIsVisible} data-testid="sticky_add_to_basket_container">
      {renderProductInfo()}
      {renderErrorMessage()}
      {renderBasketButton()}
    </Container>
  );
}

export default StickyAddToBasketButton;
